<template>
  <div @keyup.esc="$set($store.state.show, 'agent', false)" class="ai-agent-container" style="display: flex; flex-direction: column; height: 100vh; overflow: hidden;">
    <!-- Top Conversations Bar -->
    <div class="tasks-bar">
      <div class="task-tabs" v-if="msgs && msgs.length > 0">
        <button @click="createNewTask()" class="add-task-btn xs-font">+</button>
        <button
          v-for="msg in msgs"
          :key="msg.id"
          @click="switchTask(msg.id)"
          :class="['xs-font task-tab', {active: currentTask === msg.id}]"
        >
          {{ msg.topic }}
        </button>
      </div>
      <button v-else @click="createNewTask()" class="add-task-btn xs-font">+ New Task</button>
    </div>

    <!-- Main Three-Column Layout -->
    <div class="main-layout" style="flex: 1; min-height: 0; overflow: hidden;">
      <!-- Left Column: User Prompt History -->
      <div class="left-panel sm-font" style="display: flex; flex-direction: column; overflow: hidden;">
        <div class="panel-header">Prompt History</div>
        <div class="history-list" style="flex: 1; overflow-y: auto; min-height: 0;">
          <template v-if="history.prompt && history.prompt.length > 0">
            <div
              v-for="(prompt, index) in history.prompt"
              :key="`${currentTask}_${prompt}_${index}`"
              @click="selectPrompt(prompt)"
              :class="['history-item', {active: currentPrompt === prompt}]"
            >
              <div class="history-prompt">{{ getPromptText(prompt) }}</div>
              <div class="history-time">{{ formatTimestamp(prompt) }}</div>
            </div>
          </template>
          <div v-else class="history-empty">
            No prompts yet. Send a message to get started.
          </div>
        </div>
      </div>

      <!-- Center Column: AI Response Display -->
      <div class="center-panel">
        <template v-if="$store.state.terminal.lastPlan">
          <div class="plan-area">
            <!-- Phase Indicator -->
            <div class="phase-indicator">
              <div class="phase-badge" :class="`phase-${$store.state.terminal.lastPlan.phase}`">
                {{ $store.state.terminal.lastPlan.phase?.toUpperCase() || 'INTENT' }}
              </div>
            </div>
            
            <!-- Intent/Response -->
            <div class="plan-summary">
              <div class="plan-intent">
                <strong>Intent:</strong> {{ $store.state.terminal.lastPlan.intent }}
              </div>
              <div class="plan-text">{{ $store.state.terminal.lastPlan.response }}</div>
            </div>
            
            <!-- Validation Errors -->
            <div v-if="$store.state.terminal.lastPlan.validation && !$store.state.terminal.lastPlan.validation.isValid" class="validation-errors">
              <div class="validation-header">Validation Errors (Attempt {{ $store.state.terminal.lastPlan.validation.attempt || 1 }})</div>
              <div v-for="(error, index) in $store.state.terminal.lastPlan.validation.errors" :key="index" class="validation-error">
                {{ error }}
              </div>
            </div>
            
            <!-- Recommended Components -->
            <div v-if="$store.state.terminal.lastPlan.recommended_components && $store.state.terminal.lastPlan.recommended_components.length > 0" class="recommended-items">
              <div class="recommended-header">Recommended Components:</div>
              <div class="recommended-list">
                <span v-for="(comp, index) in $store.state.terminal.lastPlan.recommended_components" :key="index" class="recommended-tag">
                  {{ comp }}
                </span>
              </div>
            </div>
            
            <!-- Action Buttons for Intent Phase -->
            <div v-if="$store.state.terminal.lastPlan.phase === 'intent'" class="plan-actions">
              <button @click="$store.dispatch('agent/acceptIntent')" class="action-btn accept">✓ Accept</button>
              <button @click="$store.dispatch('agent/rejectIntent')" class="action-btn reject">✗ Reject</button>
            </div>
            
            <!-- Preview Area for Complete Phase -->
            <div v-if="$store.state.terminal.lastPlan.phase === 'complete' && $store.state.terminal.lastPlan.composedStructure && $store.state.terminal.lastPlan.composedStructure.type === 'page' && $store.state.terminal.previewPage" class="preview-area">
              <div class="preview-header">Page Preview: {{ $store.state.terminal.previewPage.name }}</div>
              <div class="preview-container" style="border: 1px solid #444; border-radius: 4px; padding: 16px; background: #1a1a1a; max-height: 600px; overflow-y: auto;">
                <ipage
                  :pageData="$store.state.terminal.previewPage.data"
                  :pagetitle="$store.state.terminal.previewPage.name"
                />
              </div>
            </div>
            
            <!-- Execute Button for Complete Phase -->
            <div v-if="$store.state.terminal.lastPlan.phase === 'complete'" class="plan-actions">
              <button @click="$store.dispatch('agent/executeComposition')" class="action-btn execute">▶ Execute</button>
            </div>
          </div>
        </template>
        <template v-else-if="$store.state.msgs[currentTask] && currentResponse">
          <div class="plan-area">
            <div class="plan-summary">
              <div class="plan-text">{{ getResponseText(currentResponse) }}</div>
            </div>
            <!-- Action Area -->
            <div class="actions-area" v-if="getResponseActions(currentResponse)">
              <div class="actions-header">Actions</div>
              <div v-for="(action, index) in getResponseActions(currentResponse)" :key="index" class="action-card">
                <div class="action-number">{{ index + 1 }}</div>
                <div class="action-details">
                  <div class="action-name">{{ action }}</div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="no-selection">No AI response yet. Send a prompt to get started.</div>
        </template>
      </div>

      <!-- Right Column: Context Panel -->
      <div class="right-panel xs-font" style="display: flex; flex-direction: column; overflow: hidden;">
        <div class="panel-header">
          Context
          <button @click="$store.commit('set', {keys: ['show', 'addContext'], val: !$store.state.show.addContext})" class="add-context-btn">+</button>
        </div>
        
        <!-- Add Context Form -->
        <div v-if="$store.state.show.addContext" class="add-context-form">
          <select v-model="$store.state.terminal.contextType" class="context-type-select">
            <option value="state">State Path</option>
            <option value="function">Function</option>
            <option value="text">Text</option>
            <option value="json">JSON</option>
          </select>
          
          <input
            v-if="$store.state.terminal.contextType === 'state'"
            v-model="$store.state.terminal.contextInput"
            placeholder="e.g., state.app.basics.items"
            class="context-input"
          />
          
          <textarea
            v-if="$store.state.terminal.contextType === 'text' || $store.state.terminal.contextType === 'json'"
            v-model="$store.state.terminal.contextInput"
            :placeholder="$store.state.terminal.contextType === 'json' ? 'Enter JSON...' : 'Enter text...'"
            class="context-textarea"
          ></textarea>
          
          <button @click="addContextItem()" class="add-btn">Add to Context</button>
        </div>
        
        <div class="context-list" style="flex: 1; overflow-y: auto; min-height: 0;">
          <div
            v-for="(item, index) in ($store.state.terminal.context || [])"
            :key="`context-${index}`"
            class="context-item"
            style="margin-bottom: 10px; padding: 10px; border: 1px solid #333; border-radius: 4px;"
          >
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <h4 style="color: #00ff00; margin: 0;">{{ item.type || 'text' }}</h4>
              <button @click="removeContextItem(index)" style="background: #aa0000; color: white; border: none; border-radius: 4px; padding: 4px 8px; cursor: pointer;">×</button>
            </div>
            <state 
              :key="$store.state.counts.state" 
              :statedata="item" 
              :thisparent="`terminal.context[${index}]`"
              editable="true"
              createable="true"
              :nesting="1"
            />
          </div>
          <div v-if="!$store.state.terminal.context || !$store.state.terminal.context.length" class="context-empty">
            No context items. Click + to add.
          </div>
        </div>
      </div>
    </div>

    <!-- User Prompt Area -->
    <div class="prompt-section">
      <div class="prompt-input-container">
        <textarea
          v-model="$store.state.terminal.inputs[currentTask]"
          @keydown.enter.ctrl="submitPrompt()"
          class="prompt-input"
          placeholder="Enter your prompt... (Ctrl+Enter to send)"
        ></textarea>
        <button @click="submitPrompt()" class="send-btn">▶</button>
      </div>
    </div>

    <!-- Footer Controls -->
    <div class="footer-controls">
      <div class="model-selector">
        <label>Model:</label>
        <select v-model="$store.state.terminal.selectedModel" class="model-dropdown">
          <option v-for="model in $store.state.terminal.availableModels || []" :key="model" :value="model">{{ model }}</option>
        </select>
        <button @click="$store.dispatch('agent/getOllamaModels')" class="refresh-btn">↻</button>
      </div>
      <div class="token-info">
        <span class="token-label">Tokens:</span>
        <span class="token-count">{{ tokenCount }}</span>
        <span class="token-separator">/</span>
        <span class="token-max">{{ modelMaxTokens }}</span>
        <span v-if="$store.state.terminal.lastTokenCount" class="last-token-count" style="margin-left: 10px; color: #00ff00;">
          (Last: {{ $store.state.terminal.lastTokenCount }})
        </span>
      </div>
      <div v-if="isLocalhost || $store.state.debug" class="debug-controls xs-font">
        <button @click="$store.dispatch('agent/introspect')" class="debug-btn">Introspect</button>
        <button @click="$store.dispatch('agent/initAgent')" class="debug-btn">Init</button>
        <button 
          v-if="$store.state.terminal.debugLogs && $store.state.terminal.debugLogs.length > 0"
          @click="$store.commit('set', {keys: ['terminal', 'showDebugConsole'], val: !$store.state.terminal.showDebugConsole})" 
          class="debug-btn"
        >
          {{ $store.state.terminal.showDebugConsole ? '⊗' : '☰' }} Debug ({{ $store.state.terminal.debugLogs.length }})
        </button>
      </div>
    </div>

    <!-- Debug Console -->
    <div v-if="$store.state.terminal.showDebugConsole" class="debug-console">
      <div class="debug-console-header">
        <span class="debug-console-title">🔍 AI System Debug Log</span>
        <button @click="$store.commit('set', {keys: ['terminal', 'debugLogs'], val: []})" class="debug-clear-btn">Clear</button>
      </div>
      <div class="debug-console-body">
          <div
            v-for="(log, index) in ($store.state.terminal.debugLogs || [])"
            :key="`log-${log.timestamp}-${index}`"
            :class="['debug-log-entry', `log-type-${log.type}`, {expanded: $store.state.terminal.expandedDebugLogs && $store.state.terminal.expandedDebugLogs[index]}]"
            @click="toggleDebugLog(index)"
          >
          <div class="debug-log-header">
            <span class="debug-log-time">{{ new Date(log.timestamp).toLocaleTimeString() }}</span>
            <span class="debug-log-type">{{ log.type }}</span>
            <span class="debug-log-summary">{{ log.summary }}</span>
          </div>
          <div v-if="$store.state.terminal.expandedDebugLogs && $store.state.terminal.expandedDebugLogs[index]" class="debug-log-details">
            <div v-if="log.data.call" class="debug-call">
              <div class="debug-call-label">CALLED:</div>
              <pre>{{ JSON.stringify(log.data.call, null, 2) }}</pre>
            </div>
            <div v-if="log.data.result" class="debug-result">
              <div class="debug-result-label">RETURNED:</div>
              <pre>{{ JSON.stringify(log.data.result, null, 2) }}</pre>
            </div>
            <div v-if="!log.data.call && !log.data.result" class="debug-other">
              <pre>{{ JSON.stringify(log.data, null, 2) }}</pre>
            </div>
          </div>
        </div>
        <div v-if="!$store.state.terminal.debugLogs || $store.state.terminal.debugLogs.length === 0" class="debug-log-empty">
          No debug logs yet. Interact with the AI to see activity.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
    name: 'agent',
    data() {
        return {
            msgs: [],
            currentTask: null,
            currentPrompt: null,
            currentResponse: null
        }
    },
    computed: {
        isLocalhost() {
            return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        },
        tokenCount() {
            const message = this.$store.state.terminal.inputs[this.currentTask] || ''
            return this.estimateTokenCount(message)
        },
        modelMaxTokens() {
            const model = this.$store.state.terminal.selectedModel
            if (!model) return '?'
            
            const modelLimits = {
                'llama3.2:latest': '128K',
                'llama3.2:1b': '128K',
                'llama3.2:3b': '128K',
                'qwen3-coder:480b-cloud': '128K',
                'deepseek-v3.1:671b-cloud': '64K',
                'gpt-oss:120b-cloud': '32K',
                'gpt-oss:latest': '32K',
                'phi4:latest': '16K',
                'smollm2:1.7b': '8K'
            }
            
            return modelLimits[model] || '?'
        },
        history() {
            if (!this.msgs || !this.currentTask) {
                return { prompt: [], response: [] };
            }

            const currentMsg = this.msgs.find(x => x.id === this.currentTask);
            if (!currentMsg || !currentMsg.banter) {
                return { prompt: [], response: [] };
            }
            return {
                prompt: Object.entries(currentMsg.banter).map(x => {
                    if (x[1].user !== this.$store.state.ai.config.agentId && x[1].user !== '5nA8rryyPedDsIvtWmTD0pRbann2') return x[0]
                }).filter(x => x !== undefined).sort((a, b) => Number(a) - Number(b)),
                response: Object.entries(currentMsg.banter).map(x => {
                    if (x[1].user === this.$store.state.ai.config.agentId || x[1].user === '5nA8rryyPedDsIvtWmTD0pRbann2') return x[0]
                }).filter(x => x !== undefined).sort((a, b) => Number(a) - Number(b))
            }
        }
    },
    methods: {
        estimateTokenCount(text) {
            if (!text) return 0
            return Math.ceil(text.length / 4)
        },
        formatTimestamp(timestamp) {
            if (!timestamp) return ''
            return new Date(parseInt(timestamp)).toLocaleTimeString()
        },
        getPromptText(prompt) {
            if (!this.currentTask || !this.$store.state.msgs[this.currentTask]) return ''
            const banter = this.$store.state.msgs[this.currentTask].banter
            if (!banter || !banter[prompt]) return ''
            const text = banter[prompt].text || ''
            return text.length > 100 ? text.substring(0, 100) + '...' : text
        },
        getResponseText(response) {
            if (!this.currentTask || !this.$store.state.msgs[this.currentTask]) return ''
            const banter = this.$store.state.msgs[this.currentTask].banter
            if (!banter || !banter[response]) return ''
            const text = banter[response].text || ''
            try {
                const parsed = JSON.parse(text)
                return parsed.text || parsed.message || parsed.response || text
            } catch (e) {
                return text
            }
        },
        getResponseActions(response) {
            if (!this.currentTask || !this.$store.state.msgs[this.currentTask]) return null
            const banter = this.$store.state.msgs[this.currentTask].banter
            if (!banter || !banter[response]) return null
            const text = banter[response].text || ''
            try {
                const parsed = JSON.parse(text)
                return parsed.options || null
            } catch (e) {
                return null
            }
        },
        switchTask(taskId) {
            this.currentTask = taskId
            if (this.currentTask) {
                this.$set(this.$store.state.terminal.inputs, this.currentTask, this.$store.state.terminal.inputs[this.currentTask] || '')
            }
            const currentMsg = this.msgs.find(x => x.id === this.currentTask)
            if (currentMsg && currentMsg.banter) {
                const banterEntries = Object.entries(currentMsg.banter).sort(([a], [b]) => Number(b) - Number(a))
                const lastUserPrompt = banterEntries.find(([, v]) => v.user !== this.$store.state.ai.config.agentId && v.user !== '5nA8rryyPedDsIvtWmTD0pRbann2')
                const lastResponse = banterEntries.find(([, v]) => v.user === this.$store.state.ai.config.agentId || v.user === '5nA8rryyPedDsIvtWmTD0pRbann2')
                if (lastUserPrompt) this.currentPrompt = lastUserPrompt[0]
                if (lastResponse) {
                    this.currentResponse = lastResponse[0]
                    // Load the plan from the response
                    if (lastResponse[1] && lastResponse[1].text) {
                        this.$store.dispatch('agent/loadPlanFromHistory', { text: lastResponse[1].text })
                    }
                } else {
                    // Clear lastPlan if no response
                    this.$store.commit('set', { keys: ['terminal', 'lastPlan'], val: null }, { root: true })
                }
            }
        },
        selectPrompt(prompt) {
            this.currentPrompt = prompt
            const currentMsg = this.msgs.find(x => x.id === this.currentTask)
            if (currentMsg && currentMsg.banter) {
                const banterEntries = Object.entries(currentMsg.banter).sort(([a], [b]) => Number(a) - Number(b))
                const promptIndex = banterEntries.findIndex(([k]) => k === prompt)
                if (promptIndex !== -1) {
                    for (let i = promptIndex + 1; i < banterEntries.length; i++) {
                        const [, v] = banterEntries[i]
                        if (v.user === this.$store.state.ai.config.agentId || v.user === '5nA8rryyPedDsIvtWmTD0pRbann2') {
                            this.currentResponse = banterEntries[i][0]
                            // Load the plan from the response
                            if (v && v.text) {
                                this.$store.dispatch('agent/loadPlanFromHistory', { text: v.text })
                            }
                            break
                        }
                    }
                }
            }
        },
        createNewTask() {
            this.$store.dispatch('agent/createConversation').then(conversationId => {
                this.$store.dispatch('query', { type: 'msgs' }).then(x => {
                    this.msgs = Object.entries(x)
                        .map(([id, val]) => (val && val.type === 'ai' ? { ...val, id } : undefined))
                        .filter(v => v !== undefined)
                        .sort((a, b) => (b.created || 0) - (a.created || 0))
                    this.currentTask = conversationId
                    if (this.currentTask) {
                        this.$set(this.$store.state.terminal.inputs, this.currentTask, '')
                    }
                })
            })
        },
        async submitPrompt() {
            const message = this.$store.state.terminal.inputs[this.currentTask]
            if (!message || !message.trim()) return
            
            if (!this.currentTask) {
                await this.createNewTask()
            }
            
            await this.$store.dispatch('agent/sendUserMessage', { 
                message: message,
                phase: 'intent'
            })
            
            this.$set(this.$store.state.terminal.inputs, this.currentTask, '')
            this.$store.dispatch('query', { type: 'msgs' }).then(x => {
                this.msgs = Object.entries(x)
                    .map(([id, val]) => (val && val.type === 'ai' ? { ...val, id } : undefined))
                    .filter(v => v !== undefined)
                    .sort((a, b) => (b.created || 0) - (a.created || 0))
                
                const currentMsg = this.msgs.find(x => x.id === this.currentTask)
                if (currentMsg && currentMsg.banter) {
                    const banterEntries = Object.entries(currentMsg.banter).sort(([a], [b]) => Number(b) - Number(a))
                    const lastUserPrompt = banterEntries.find(([, v]) => v.user !== this.$store.state.ai.config.agentId && v.user !== '5nA8rryyPedDsIvtWmTD0pRbann2')
                    const lastResponse = banterEntries.find(([, v]) => v.user === this.$store.state.ai.config.agentId || v.user === '5nA8rryyPedDsIvtWmTD0pRbann2')
                    if (lastUserPrompt) this.currentPrompt = lastUserPrompt[0]
                    if (lastResponse) this.currentResponse = lastResponse[0]
                }
            })
        },
        addContextItem() {
            const type = this.$store.state.terminal.contextType
            const input = this.$store.state.terminal.contextInput
            
            if (!input) return
            
            let value = input
            let label = input
            
            if (type === 'state') {
                const path = input.replace('state.', '').split('.')
                value = path.reduce((obj, key) => obj?.[key], this.$store.state)
                label = `State: ${input}`
            } else if (type === 'json') {
                try {
                    value = JSON.parse(input)
                    label = 'JSON Data'
                } catch (e) {
                    alert('Invalid JSON')
                    return
                }
            } else if (type === 'function') {
                label = `Function: ${input}`
            }
            
            const items = this.$store.state.terminal.context || []
            items.push({ type, label, value, id: Date.now() })
            
            this.$store.commit('set', {
                keys: ['terminal', 'context'],
                val: items
            })
            
            this.$store.commit('set', {keys: ['terminal', 'contextInput'], val: ''})
            this.$store.commit('set', {keys: ['show', 'addContext'], val: false})
        },
        removeContextItem(index) {
            const items = this.$store.state.terminal.context || []
            items.splice(index, 1)
            this.$store.commit('set', {
                keys: ['terminal', 'context'],
                val: items
            })
        },
        toggleDebugLog(index) {
            const expandedLogs = this.$store.state.terminal.expandedDebugLogs || []
            const current = expandedLogs[index]
            this.$store.commit('set', {
                keys: ['terminal', 'expandedDebugLogs', index],
                val: !current
            })
            this.$forceUpdate()
        }
    },
    props: {},
    async mounted() {
        await this.$store.dispatch('agent/initAgent')
        await this.$store.dispatch('agent/getOllamaModels')
        
        await this.$store.dispatch('query', { type: 'msgs' })
        
        this.msgs = Object.entries(this.$store.state.msgs || {})
            .map(([id, val]) => (val && val.type === 'ai' ? { ...val, id } : undefined))
            .filter(v => v !== undefined)
            .sort((a, b) => (b.created || 0) - (a.created || 0))
        
        if (this.msgs.length > 0 && !this.currentTask) {
            this.currentTask = this.msgs[0].id
            this.$set(this.$store.state.terminal.inputs, this.currentTask, '')
            
            const currentMsg = this.msgs[0]
            if (currentMsg && currentMsg.banter) {
                const banterEntries = Object.entries(currentMsg.banter).sort(([a], [b]) => Number(b) - Number(a))
                const lastUserPrompt = banterEntries.find(([, v]) => v.user !== this.$store.state.ai.config.agentId && v.user !== '5nA8rryyPedDsIvtWmTD0pRbann2')
                const lastResponse = banterEntries.find(([, v]) => v.user === this.$store.state.ai.config.agentId || v.user === '5nA8rryyPedDsIvtWmTD0pRbann2')
                if (lastUserPrompt) this.currentPrompt = lastUserPrompt[0]
                if (lastResponse) this.currentResponse = lastResponse[0]
            }
        }
    }
}
</script>

<style scoped>
.ai-agent-container {
  background: #0a0a0a;
  color: #00ff00;
  font-family: 'Courier New', monospace;
  border: 1px solid #333;
  border-radius: 4px;
}

.tasks-bar {
  background: #111;
  padding: 8px;
  border-bottom: 1px solid #333;
}

.task-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
}

.task-tab {
  padding: 8px 16px;
  background: #222;
  border: 1px solid #333;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  color: #00ff00;
}

.task-tab:hover {
  background: #2a2a2a;
  border-color: #00ff00;
}

.task-tab.active {
  background: #00ff00;
  color: #000;
  border-color: #00ff00;
}

.add-task-btn {
  padding: 8px 16px;
  background: #00ff00;
  color: #000;
  border: none;
  cursor: pointer;
  font-weight: bold;
}

.main-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.left-panel,
.right-panel {
  width: 250px;
  background: #0f0f0f;
  border-right: 1px solid #333;
  display: flex;
  flex-direction: column;
}

.right-panel {
  border-right: none;
  border-left: 1px solid #333;
}

.panel-header {
  padding: 12px;
  background: #1a1a1a;
  border-bottom: 1px solid #333;
  font-weight: bold;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.history-list,
.context-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.history-item {
  padding: 8px;
  margin-bottom: 4px;
  background: #1a1a1a;
  border: 1px solid #222;
  cursor: pointer;
  transition: all 0.2s;
}

.history-item:hover {
  background: #222;
  border-color: #00ff00;
}

.history-item.active {
  background: #00ff00;
  color: #000;
  border-color: #00ff00;
}

.history-prompt {
  color: #00ff00;
  margin-bottom: 4px;
  font-size: 12px;
}

.history-time {
  font-size: 8px;
  color: #666;
}

.history-empty {
  padding: 16px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.center-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0a0a0a;
}

.plan-area,
.no-selection {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.plan-summary {
  padding: 16px;
  background: #1a1a1a;
  border: 1px solid #333;
  margin-bottom: 16px;
}

.plan-text {
  color: #00ff00;
  line-height: 1.6;
}

.plan-intent {
  color: #00ff00;
  margin-bottom: 12px;
  font-weight: bold;
}

.phase-indicator {
  margin-bottom: 16px;
}

.phase-badge {
  display: inline-block;
  padding: 6px 12px;
  background: #333;
  color: #00ff00;
  border: 1px solid #00ff00;
  border-radius: 4px;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 12px;
}

.phase-badge.phase-intent {
  background: #1a3a1a;
  border-color: #00ff00;
}

.phase-badge.phase-compose {
  background: #3a3a1a;
  border-color: #ffff00;
  color: #ffff00;
}

.phase-badge.phase-complete {
  background: #1a3a3a;
  border-color: #00ffff;
  color: #00ffff;
}

.validation-errors {
  margin-bottom: 16px;
  padding: 12px;
  background: #3a1a1a;
  border: 1px solid #ff0000;
  border-radius: 4px;
}

.validation-header {
  color: #ff0000;
  font-weight: bold;
  margin-bottom: 8px;
  text-transform: uppercase;
  font-size: 12px;
}

.validation-error {
  color: #ff6666;
  margin-bottom: 4px;
  font-size: 12px;
  padding-left: 12px;
}

.recommended-items {
  margin-bottom: 16px;
  padding: 12px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
}

.recommended-header {
  color: #00ff00;
  font-weight: bold;
  margin-bottom: 8px;
  text-transform: uppercase;
  font-size: 12px;
}

.recommended-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.recommended-tag {
  display: inline-block;
  padding: 4px 8px;
  background: #2a2a2a;
  color: #00ff00;
  border: 1px solid #00ff00;
  border-radius: 4px;
  font-size: 11px;
}

.plan-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}

.action-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  text-transform: uppercase;
  transition: all 0.2s;
}

.action-btn.accept {
  background: #00ff00;
  color: #000;
}

.action-btn.accept:hover {
  background: #00dd00;
}

.action-btn.reject {
  background: #ff0000;
  color: #fff;
}

.action-btn.reject:hover {
  background: #dd0000;
}

.action-btn.execute {
  background: #00ffff;
  color: #000;
}

.action-btn.execute:hover {
  background: #00dddd;
}

.preview-area {
  margin-top: 16px;
  margin-bottom: 16px;
}

.preview-header {
  padding: 8px 12px;
  background: #1a3a3a;
  color: #00ffff;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 12px;
  border: 1px solid #00ffff;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
}

.preview-container {
  border: 1px solid #00ffff;
  border-top: none;
  border-radius: 0 0 4px 4px;
}

.actions-area {
  margin-bottom: 16px;
}

.actions-header {
  padding: 8px 12px;
  background: #00ff00;
  color: #000;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.action-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #1a1a1a;
  border: 1px solid #333;
  margin-bottom: 8px;
  transition: all 0.2s;
}

.action-card:hover {
  background: #222;
  border-color: #00ff00;
}

.action-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #00ff00;
  color: #000;
  font-weight: bold;
  border-radius: 50%;
}

.action-details {
  flex: 1;
}

.action-name {
  color: #00ff00;
  font-weight: bold;
  margin-bottom: 4px;
}

.no-selection {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  font-style: italic;
}

.prompt-section {
  padding: 16px;
  background: #111;
  border-top: 1px solid #333;
}

.prompt-input-container {
  display: flex;
  gap: 8px;
}

.prompt-input {
  flex: 1;
  background: #0a0a0a;
  border: 1px solid #333;
  color: #00ff00;
  padding: 12px;
  font-family: 'Courier New', monospace;
  resize: none;
  min-height: 40px;
  max-height: 200px;
}

.prompt-input:focus {
  outline: none;
  border-color: #00ff00;
}

.send-btn {
  padding: 12px 24px;
  background: #00ff00;
  color: #000;
  border: none;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
}

.send-btn:hover {
  background: #00dd00;
}

.context-item {
  padding: 8px;
  margin-bottom: 4px;
  background: #1a1a1a;
  border: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
}

.add-context-btn {
  background: #00ff00;
  color: #000;
  border: none;
  padding: 2px 8px;
  cursor: pointer;
  font-weight: bold;
  margin-left: auto;
}

.add-context-form {
  padding: 8px;
  background: #1a1a1a;
  border-bottom: 1px solid #333;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.context-type-select,
.context-function-select {
  background: #0a0a0a;
  color: #00ff00;
  border: 1px solid #333;
  padding: 6px;
  font-family: 'Courier New', monospace;
}

.context-input {
  background: #0a0a0a;
  color: #00ff00;
  border: 1px solid #333;
  padding: 6px;
  font-family: 'Courier New', monospace;
}

.context-textarea {
  background: #0a0a0a;
  color: #00ff00;
  border: 1px solid #333;
  padding: 6px;
  font-family: 'Courier New', monospace;
  min-height: 60px;
  resize: vertical;
}

.add-btn {
  background: #00ff00;
  color: #000;
  border: none;
  padding: 6px 12px;
  cursor: pointer;
  font-weight: bold;
  text-transform: uppercase;
}

.context-empty {
  padding: 16px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.footer-controls {
  padding: 12px;
  background: #111;
  border-top: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.model-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.model-selector label {
  color: #00ff00;
  text-transform: uppercase;
}

.model-dropdown {
  background: #0a0a0a;
  color: #00ff00;
  border: 1px solid #333;
  padding: 6px 12px;
  font-family: 'Courier New', monospace;
}

.token-info {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}

.token-label {
  color: #666;
}

.token-count {
  color: #00ff00;
  font-weight: bold;
}

.token-separator {
  color: #333;
}

.token-max {
  color: #999;
}

.refresh-btn,
.debug-btn {
  padding: 6px 12px;
  background: #222;
  color: #00ff00;
  border: 1px solid #333;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  transition: all 0.2s;
}

.refresh-btn:hover,
.debug-btn:hover {
  background: #2a2a2a;
  border-color: #00ff00;
}

.debug-controls {
  display: flex;
  gap: 8px;
}

.debug-console {
  width: 100%;
  height: 300px;
  background: #0a0a0a;
  border-top: 2px solid #00ff00;
  display: flex;
  flex-direction: column;
  margin-top: 8px;
}

.debug-console-header {
  padding: 8px 12px;
  background: #1a1a1a;
  border-bottom: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.debug-console-title {
  color: #00ff00;
  font-weight: bold;
  text-transform: uppercase;
}

.debug-clear-btn {
  background: #ff0000;
  color: #fff;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-weight: bold;
}

.debug-console-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.debug-log-entry {
  margin-bottom: 4px;
  border: 1px solid #333;
  background: #0f0f0f;
  cursor: pointer;
  transition: all 0.2s;
}

.debug-log-entry:hover {
  border-color: #00ff00;
}

.debug-log-entry.expanded {
  border-color: #00ff00;
  background: #1a1a1a;
}

.debug-log-header {
  padding: 6px 8px;
  display: flex;
  gap: 12px;
  align-items: center;
}

.debug-log-time {
  color: #666;
  font-size: 8px;
  min-width: 80px;
}

.debug-log-type {
  color: #00ff00;
  font-size: 8px;
  font-weight: bold;
  text-transform: uppercase;
  min-width: 100px;
}

.debug-log-summary {
  color: #999;
  flex: 1;
}

.debug-log-details {
  padding: 8px;
  border-top: 1px solid #333;
  background: #0a0a0a;
}

.debug-call,
.debug-result,
.debug-other {
  margin-bottom: 8px;
}

.debug-call-label,
.debug-result-label {
  color: #00ff00;
  font-weight: bold;
  margin-bottom: 4px;
}

.debug-call pre,
.debug-result pre,
.debug-other pre {
  color: #999;
  font-size: 8px;
  margin: 0;
  overflow-x: auto;
}

.debug-log-empty {
  color: #666;
  font-style: italic;
  text-align: center;
  padding: 32px;
}

.log-type-init { border-left: 3px solid #00ff00; }
.log-type-introspect { border-left: 3px solid #0099ff; }
.log-type-conversation { border-left: 3px solid #ff00ff; }
.log-type-prompt { border-left: 3px solid #ffff00; }
.log-type-ollama { border-left: 3px solid #ff9900; }
.log-type-response { border-left: 3px solid #00ffff; }
.log-type-error { border-left: 3px solid #ff0000; }
.log-type-choice { border-left: 3px solid #00ff99; }
.log-type-helper { border-left: 3px solid #9900ff; }
.log-type-converse { border-left: 3px solid #ff9900; }

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #0a0a0a;
}

::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #444;
}
</style>
